/**
 * Schémas Zod du module Vendange (Lot C.1) — partagés API ⇄ ERP.
 *
 * Cœur du Lot C : pesées de raisin à la réception + ventilation entre
 * sous-parcelles + bordereaux d'apport coopérative (flux RC).
 *
 * Deux flux dans une SEULE table `pesees` (champ `destination`, cf
 * `packages/database/src/schema/pesees.ts`) :
 *   - RM  → `vinification_propre`   : raisin pressuré au domaine (rattaché à
 *     un marc en C.2).
 *   - RC  → `livraison_cooperative` : raisin livré à la coop ; le détail reçu
 *     (kg par cru/cépage) est saisi via `bordereau_apport`.
 *   - vente_raisin / vente_mout     : flux négoce.
 *
 * Source des énumérations : CHECK SQL des tables `pesees` /
 * `bordereaux_apport` (alignement strict, ne pas diverger de la contrainte).
 *
 * Règles métier clés (cf `docs/features/lot-c-vinification/01-cadrage-metier.md`
 * + commentaires des schémas Drizzle) :
 *   - `poids_kg` strictement positif (poids NET, tare déjà déduite).
 *   - Si ventilation fournie : Σ `poids_reparti_kg` = `poids_kg` (tolérance
 *     décimale gérée côté use case/invariant).
 *   - Rendement kg/ha : ALERTE applicative seulement (jamais blocage) ;
 *     `rendement_max_kg_ha` lu sur la campagne, jamais hardcodé.
 *   - Immutabilité : une pesée validée ne se modifie pas → correction par
 *     opération inverse horodatée (registre inaltérable).
 *
 * Convention : poids en KG (NUMERIC(10,2) → string Drizzle, précision
 * préservée au transport). `cru_commune_insee` = code INSEE commune (5 chars).
 */

import { z } from "zod";

import { communeInseeStrictSchema, idSchema } from "./parcellaire.js";

/** Code commune INSEE (5 chars) — réutilise la validation cadastre du parcellaire. */
export { communeInseeStrictSchema as cruCommuneInseeSchema } from "./parcellaire.js";

// ────────────────────────────────────────────────────────────────────────────
// Bornes métier — poids
// ────────────────────────────────────────────────────────────────────────────

/** Poids minimal accepté : > 0 (une pesée a forcément du raisin). */
export const POIDS_KG_MIN = 0.01;
/**
 * Poids maximal accepté (garde-fou de saisie) : 1 000 000 kg. La colonne
 * NUMERIC(10,2) accepte ~99 999 999 kg ; aucune pesée réelle n'atteint ce
 * volume. Réajustable si besoin.
 */
export const POIDS_KG_MAX = 1_000_000;

/** Nombre maximal de contenants par pesée (garde-fou de saisie). */
export const NB_CONTENANTS_MAX = 100_000;

// ────────────────────────────────────────────────────────────────────────────
// Énumérations (miroir des CHECK SQL)
// ────────────────────────────────────────────────────────────────────────────

/** Type de contenant pesé — aligné sur CHECK SQL `pesees.contenant`. */
export const contenantSchema = z.enum(["caisse", "mannequin", "benne", "autre"]);
export type Contenant = z.infer<typeof contenantSchema>;

/** Destination du raisin pesé — aligné sur CHECK SQL `pesees.destination`. */
export const destinationSchema = z.enum([
  "vinification_propre",
  "livraison_cooperative",
  "vente_raisin",
  "vente_mout",
]);
export type Destination = z.infer<typeof destinationSchema>;

/** Cycle de vie d'une pesée — aligné sur CHECK SQL `pesees.statut`. */
export const peseeStatutSchema = z.enum(["brute", "validee", "corrigee", "annulee"]);
export type PeseeStatut = z.infer<typeof peseeStatutSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques
// ────────────────────────────────────────────────────────────────────────────

/** Poids net en kg, strictement positif et borné (garde-fou de saisie). */
export const poidsKgSchema = z
  .number({
    invalid_type_error: "Poids invalide.",
    required_error: "Poids requis.",
  })
  .positive("Le poids doit être supérieur à 0.")
  .min(POIDS_KG_MIN, `Poids minimum : ${POIDS_KG_MIN} kg.`)
  .max(POIDS_KG_MAX, `Poids maximum : ${POIDS_KG_MAX} kg.`);

/** Tare en kg, ≥ 0 (informative : `poids_kg` est déjà net). */
export const tareKgSchema = z
  .number({ invalid_type_error: "Tare invalide." })
  .min(0, "La tare ne peut pas être négative.")
  .max(POIDS_KG_MAX, `Tare maximum : ${POIDS_KG_MAX} kg.`);

/** Date au format ISO `YYYY-MM-DD`. */
export const dateIsoSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date invalide (format attendu AAAA-MM-JJ).");

/** Heure au format `HH:MM` (24 h), suffixe `:SS` optionnel. */
export const heureSchema = z
  .string()
  .trim()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, "Heure invalide (format attendu HH:MM).");

/** Texte libre court optionnel — vide normalisé en undefined. */
const optionalShortText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} trop long (${max} caractères max).`)
    .optional()
    .or(z.literal("").transform(() => undefined));

/** Notes libres optionnelles (4000 chars). */
const optionalNotes = z
  .string()
  .trim()
  .max(4000, "Notes trop longues (4000 caractères max).")
  .optional()
  .or(z.literal("").transform(() => undefined));

// ────────────────────────────────────────────────────────────────────────────
// Ventilation (1 pesée → N sous-parcelles)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Une ligne de ventilation : part de poids attribuée à une SPCV. Invariant
 * (use case) : Σ `poids_reparti_kg` = `poids_kg` de la pesée parente.
 */
export const ventilationLigneSchema = z.object({
  sous_parcelle_id: idSchema,
  poids_reparti_kg: poidsKgSchema,
});
export type VentilationLigne = z.infer<typeof ventilationLigneSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Pesée — création
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `vendange.creerPesee` — saisie d'une pesée de raisin à la réception.
 *
 * `cru_commune_insee` optionnel (dérivable de la parcelle). `ventilation`
 * optionnelle : si fournie, la somme des parts doit égaler `poids_kg` (vérifié
 * côté use case via `assertVentilationCoherente`). `sous_parcelle_id` simple et
 * `ventilation` sont mutuellement exclusifs (cf refine).
 */
export const peseeCreerInput = z
  .object({
    campagne_id: idSchema,
    parcelle_id: idSchema,
    /** SPCV d'origine (porte le cépage). Mutuellement exclusif avec `ventilation`. */
    sous_parcelle_id: idSchema.optional(),
    cepage_id: idSchema,
    /** Cru/commune de provenance (code INSEE), optionnel — dérivable de la parcelle. */
    cru_commune_insee: communeInseeStrictSchema.optional(),

    date_pesee: dateIsoSchema,
    heure_pesee: heureSchema.optional().or(z.literal("").transform(() => undefined)),

    poids_kg: poidsKgSchema,
    contenant: contenantSchema,
    nb_contenants: z
      .number()
      .int("Le nombre de contenants doit être entier.")
      .min(1, "Au moins un contenant.")
      .max(NB_CONTENANTS_MAX, `Nombre de contenants maximum : ${NB_CONTENANTS_MAX}.`)
      .optional(),
    tare_kg: tareKgSchema.optional(),

    /** Opérateur ayant réalisé la pesée (utilisateur global). */
    operateur_id: idSchema.optional(),
    /** N° de bon de pesée / ticket balance. */
    numero_bon: optionalShortText(40, "Numéro de bon"),

    destination: destinationSchema,
    notes: optionalNotes,

    /** Répartition fine entre SPCV (1 pesée → N SPCV). */
    ventilation: z.array(ventilationLigneSchema).min(1).optional(),
  })
  .superRefine((value, ctx) => {
    // `sous_parcelle_id` simple et `ventilation` ne se cumulent pas : soit une
    // origine SPCV unique, soit une répartition multi-SPCV (jamais les deux).
    if (value.sous_parcelle_id && value.ventilation && value.ventilation.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ventilation"],
        message:
          "Indiquez soit une sous-parcelle unique, soit une ventilation entre plusieurs sous-parcelles, pas les deux.",
      });
    }
    // Cohérence Σ ventilation = poids_kg (vérification de format ; l'invariant
    // métier est re-checké côté use case avec la tolérance décimale).
    if (value.ventilation && value.ventilation.length > 0) {
      const somme = value.ventilation.reduce((acc, ligne) => acc + ligne.poids_reparti_kg, 0);
      // Tolérance 0,01 kg (précision NUMERIC(10,2)) pour absorber l'arrondi.
      if (Math.abs(somme - value.poids_kg) > 0.01) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ventilation"],
          message: `La somme des parts ventilées (${somme} kg) doit égaler le poids pesé (${value.poids_kg} kg).`,
        });
      }
    }
  });
export type PeseeCreerInput = z.infer<typeof peseeCreerInput>;

/** Poids saisi exactement au centième de kg, sans arrondi silencieux. */
export const poidsExactKgSchema = z
  .string()
  .trim()
  .regex(/^\d{1,7}(?:[.,]\d{1,2})?$/, "Indiquez un poids avec deux décimales maximum.")
  .transform((valeur) => valeur.replace(",", "."))
  .pipe(
    z
      .string()
      .refine((valeur) => lireCentiKg(valeur) <= BigInt(POIDS_KG_MAX) * 100n, "Poids trop élevé.")
      .transform((valeur) => formatCentiKg(lireCentiKg(valeur))),
  );

function lireCentiKg(valeur: string): bigint {
  const [entier, decimales = ""] = valeur.split(".");
  return BigInt(entier!) * 100n + BigInt(decimales.padEnd(2, "0"));
}

function formatCentiKg(valeur: bigint): string {
  const absolue = valeur < 0n ? -valeur : valeur;
  return `${valeur < 0n ? "-" : ""}${absolue / 100n}.${(absolue % 100n).toString().padStart(2, "0")}`;
}

export const ligneTareCaisseSchema = z.object({
  modele_id: idSchema,
  libelle: z.string().trim().min(1).max(80),
  tare_unitaire_kg: poidsExactKgSchema,
  nombre: z.number().int().min(1).max(NB_CONTENANTS_MAX),
});
export type LigneTareCaisse = z.infer<typeof ligneTareCaisseSchema>;

export const taresCaissesSchema = z
  .object({
    version: z.literal(1),
    lignes: z.array(ligneTareCaisseSchema).min(1, "Ajoutez les caisses pesées.").max(30),
  })
  .superRefine(({ lignes }, ctx) => {
    if (new Set(lignes.map((ligne) => ligne.modele_id)).size !== lignes.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lignes"],
        message: "Regroupez les caisses du même modèle sur une seule ligne.",
      });
    if (lignes.reduce((somme, ligne) => somme + ligne.nombre, 0) > NB_CONTENANTS_MAX)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lignes"],
        message: "Nombre total de caisses trop élevé.",
      });
  });
export type TaresCaisses = z.infer<typeof taresCaissesSchema>;

/** Même calcul pour l'aperçu et l'enregistrement ; le serveur vérifie les modèles. */
export function calculerPeseeCaisses(poidsBrut: string, tares: TaresCaisses) {
  const brut = lireCentiKg(poidsExactKgSchema.parse(poidsBrut));
  const lignes = taresCaissesSchema.parse(tares).lignes;
  const tare = lignes.reduce(
    (total, ligne) => total + lireCentiKg(ligne.tare_unitaire_kg) * BigInt(ligne.nombre),
    0n,
  );
  return {
    poids_brut_kg: formatCentiKg(brut),
    tare_totale_kg: formatCentiKg(tare),
    poids_net_kg: formatCentiKg(brut - tare),
    nb_caisses: lignes.reduce((total, ligne) => total + ligne.nombre, 0),
  };
}

/** Nouvelle saisie : brut et caisses constatés, aucune destination présumée. */
export const peseeCaissesCreerInput = peseeCreerInput
  .innerType()
  .omit({
    poids_kg: true,
    contenant: true,
    nb_contenants: true,
    tare_kg: true,
    destination: true,
  })
  .extend({
    schema_version: z.literal(2),
    uuid_commande: z.string().uuid(),
    auteur_saisie_id: idSchema,
    poids_brut_kg: poidsExactKgSchema,
    tares_caisses: taresCaissesSchema,
  })
  .superRefine((valeur, ctx) => {
    if (valeur.sous_parcelle_id && valeur.ventilation?.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ventilation"],
        message: "Choisissez une sous-parcelle ou une ventilation, pas les deux.",
      });
    if (
      !poidsExactKgSchema.safeParse(valeur.poids_brut_kg).success ||
      !taresCaissesSchema.safeParse(valeur.tares_caisses).success
    )
      return;
    const bilan = calculerPeseeCaisses(valeur.poids_brut_kg, valeur.tares_caisses);
    if (bilan.poids_net_kg.startsWith("-") || bilan.poids_net_kg === "0.00") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["poids_brut_kg"],
        message: "Le poids brut doit dépasser le total des tares.",
      });
      return;
    }
    if (valeur.ventilation?.length) {
      const parts = valeur.ventilation.map((ligne) =>
        poidsExactKgSchema.safeParse(String(ligne.poids_reparti_kg)),
      );
      if (
        parts.some((part) => !part.success) ||
        parts.reduce((somme, part) => somme + (part.success ? lireCentiKg(part.data) : 0n), 0n) !==
          lireCentiKg(bilan.poids_net_kg)
      )
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ventilation"],
          message: "La ventilation doit correspondre exactement au poids net, au centième de kg.",
        });
    }
  });
export type PeseeCaissesCreerInput = z.infer<typeof peseeCaissesCreerInput>;
export const peseeSaisieInput = z.union([
  peseeCaissesCreerInput,
  z.intersection(z.object({ schema_version: z.undefined() }), peseeCreerInput),
]);
export type PeseeSaisieInput = z.infer<typeof peseeSaisieInput>;

export const modeleCaisseCreerInput = z.object({
  libelle: z.string().trim().min(1, "Nommez ce modèle de caisse.").max(80),
  tare_unitaire_kg: poidsExactKgSchema.refine(
    (valeur) => lireCentiKg(valeur) > 0n,
    "La tare doit être supérieure à zéro.",
  ),
  notes: optionalNotes,
});
export const modeleCaisseSortieSchema = z.object({
  id: idSchema,
  libelle: z.string(),
  tare_unitaire_kg: z.string(),
  notes: z.string().nullable(),
  archive: z.boolean(),
});
export type ModeleCaisseSortie = z.infer<typeof modeleCaisseSortieSchema>;
export const modeleCaisseArchiverInput = z.object({ id: idSchema, archive: z.boolean() });

// ────────────────────────────────────────────────────────────────────────────
// Pesée — lecture / validation / correction
// ────────────────────────────────────────────────────────────────────────────

/** Input `vendange.getPesee` — fiche détail d'une pesée + ventilation. */
export const peseeGetByIdInput = z.object({ id: idSchema });
export type PeseeGetByIdInput = z.infer<typeof peseeGetByIdInput>;

/**
 * Input `vendange.listerPesees` — filtres optionnels pour la liste tenant.
 * `groupe` : si vrai, l'API renvoie aussi un regroupement (par cépage/cru/
 * destination) en plus de la liste plate (pour les widgets dashboard).
 */
export const peseeListerInput = z
  .object({
    campagne_id: idSchema.optional(),
    parcelle_id: idSchema.optional(),
    destination: destinationSchema.optional(),
    statut: peseeStatutSchema.optional(),
    groupe: z.boolean().optional().default(false),
  })
  .optional();
export type PeseeListerInput = z.infer<typeof peseeListerInput>;

/**
 * Input `vendange.validerPesee` — passage `brute → validee`. Action qui
 * comptabilise la pesée pour la Déclaration de Récolte (irréversible : une
 * pesée validée ne se modifie plus, correction par opération inverse).
 */
export const peseeValiderInput = z.object({ id: idSchema });
export type PeseeValiderInput = z.infer<typeof peseeValiderInput>;

/**
 * Input `vendange.corrigerPesee` — correction par OPÉRATION INVERSE (jamais un
 * UPDATE destructif). On ne modifie pas la pesée d'origine : on la marque
 * `corrigee` et on enregistre une nouvelle pesée portant les valeurs corrigées
 * (registre inaltérable).
 *
 * `pesee_id` = la pesée à corriger. `correction` = le payload de la nouvelle
 * pesée de remplacement (même forme que `peseeCreerInput`).
 */
export const peseeCorrigerInput = z.object({
  pesee_id: idSchema,
  correction: peseeCreerInput,
  /** Motif de la correction (traçabilité). */
  motif: optionalShortText(200, "Motif"),
});
export type PeseeCorrigerInput = z.infer<typeof peseeCorrigerInput>;
export const peseeCaissesCorrigerInput = z.object({
  pesee_id: idSchema,
  correction: peseeCaissesCreerInput,
  motif: z.string().trim().min(1, "Indiquez le motif de correction.").max(200),
});
export type PeseeCaissesCorrigerInput = z.infer<typeof peseeCaissesCorrigerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Tickets de pesée extérieurs (anciennement bordereaux d'apport coopérative)
// ────────────────────────────────────────────────────────────────────────────

/** Ligne historique v1 : poids obligatoire par couple cépage/cru. */
export const bordereauApportLigneSchema = z.object({
  cepage_id: idSchema,
  cru_commune_insee: communeInseeStrictSchema.optional(),
  poids_kg: poidsKgSchema,
});
export type BordereauApportLigneInput = z.infer<typeof bordereauApportLigneSchema>;

/**
 * Ancien payload conservé pour les PWA déjà installées. Il reste interprété
 * comme un ticket v1 ; son poids officiel est la somme exacte de ses lignes.
 */
export const bordereauApportCreerV1Input = z.object({
  schema_version: z.undefined().optional(),
  campagne_id: idSchema,
  cooperative: z
    .string()
    .trim()
    .min(1, "Nom de la coopérative requis.")
    .max(120, "Nom de coopérative trop long (120 caractères max)."),
  numero: optionalShortText(40, "Numéro de bordereau"),
  date_apport: dateIsoSchema,
  lignes: z.array(bordereauApportLigneSchema).min(1, "Au moins une ligne d'apport."),
  notes: optionalNotes,
});
export type BordereauApportCreerV1Input = z.infer<typeof bordereauApportCreerV1Input>;

/** Usage constaté du ticket extérieur ; aucune conséquence liquide automatique. */
export const ticketExterieurUsageSchema = z.enum([
  "non_precise",
  "apport_sans_restitution",
  "pressurage_a_facon",
]);
export type TicketExterieurUsage = z.infer<typeof ticketExterieurUsageSchema>;

/**
 * Origine facultative telle qu'elle est réellement indiquée sur le ticket.
 * Aucun détail n'est inventé : une ligne doit identifier au moins une origine
 * (libellé, cru, parcelle ou cépage) et peut porter un poids partiel positif.
 */
export const ticketExterieurOrigineSchema = z
  .object({
    libelle_origine: optionalShortText(160, "Libellé d'origine"),
    parcelle_id: idSchema.optional(),
    sous_parcelle_id: idSchema.optional(),
    cepage_id: idSchema.optional(),
    cru_commune_insee: communeInseeStrictSchema.optional(),
    poids_kg: poidsExactKgSchema
      .refine((poids) => lireCentiKg(poids) > 0n, "Le poids ventilé doit être supérieur à zéro.")
      .optional(),
  })
  .superRefine((origine, ctx) => {
    if (origine.sous_parcelle_id && !origine.parcelle_id)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["parcelle_id"],
        message: "La parcelle est requise avec une sous-parcelle.",
      });
    if (
      !origine.libelle_origine &&
      !origine.parcelle_id &&
      !origine.sous_parcelle_id &&
      !origine.cepage_id &&
      !origine.cru_commune_insee
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Renseignez au moins une information d'origine.",
      });
  });
export type TicketExterieurOrigineInput = z.infer<typeof ticketExterieurOrigineSchema>;

/** Champs v2 réutilisés par la création et le remplacement. */
const ticketExterieurCreerV2Base = z.object({
  schema_version: z.literal(2),
  uuid_commande: z.string().uuid(),
  /** Identité de la file hors-ligne ; revalidée contre la session par l'API. */
  auteur_membre_id: idSchema,
  campagne_id: idSchema,
  emetteur: z.string().trim().min(1, "Émetteur requis.").max(120),
  site_emetteur: optionalShortText(120, "Site émetteur"),
  operateur_apporteur: optionalShortText(160, "Opérateur apporteur"),
  revendications_particulieres: optionalShortText(1000, "Revendications particulières"),
  numero: optionalShortText(40, "Numéro de ticket"),
  date_pesee: dateIsoSchema,
  heure_pesee: heureSchema.optional().or(z.literal("").transform(() => undefined)),
  usage: ticketExterieurUsageSchema.default("non_precise"),
  poids_net_kg: poidsExactKgSchema.refine(
    (poids) => lireCentiKg(poids) > 0n,
    "Le poids net doit être supérieur à zéro.",
  ),
  origines: z.array(ticketExterieurOrigineSchema).max(100).default([]),
  notes: optionalNotes,
});

/** Nouveau ticket : le poids net officiel de l'en-tête est la source de vérité. */
export const ticketExterieurCreerV2Input = ticketExterieurCreerV2Base.superRefine(
  ({ poids_net_kg, origines }, ctx) => {
    const poidsVentile = origines.reduce(
      (somme, origine) => somme + (origine.poids_kg ? lireCentiKg(origine.poids_kg) : 0n),
      0n,
    );
    if (poidsVentile > lireCentiKg(poids_net_kg))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["origines"],
        message: "Le poids détaillé ne peut pas dépasser le poids net officiel du ticket.",
      });
  },
);
export type TicketExterieurCreerV2Input = z.infer<typeof ticketExterieurCreerV2Input>;

/** Compatibilité API : v2 explicite ou ancien format sans version. */
export const bordereauApportCreerInput = z.union([
  ticketExterieurCreerV2Input,
  bordereauApportCreerV1Input,
]);
export type BordereauApportCreerInput = z.infer<typeof bordereauApportCreerInput>;

export const ticketExterieurRectificationNatureSchema = z.enum(["annulation", "rectification"]);

/** Le remplacement reprend les champs v2 ; l'UUID et l'auteur sont portés par l'opération. */
const ticketExterieurRemplacementSchema = ticketExterieurCreerV2Base
  .omit({
    uuid_commande: true,
    auteur_membre_id: true,
  })
  .superRefine(({ poids_net_kg, origines }, ctx) => {
    const poidsVentile = origines.reduce(
      (somme, origine) => somme + (origine.poids_kg ? lireCentiKg(origine.poids_kg) : 0n),
      0n,
    );
    if (poidsVentile > lireCentiKg(poids_net_kg))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["origines"],
        message: "Le poids détaillé ne peut pas dépasser le poids net officiel du ticket.",
      });
  });

export const bordereauApportRectifierInput = z
  .object({
    bordereau_id: idSchema,
    nature: ticketExterieurRectificationNatureSchema,
    motif: z.string().trim().min(1, "Indiquez le motif.").max(500),
    uuid_commande: z.string().uuid(),
    auteur_membre_id: idSchema,
    remplacement: ticketExterieurRemplacementSchema.optional(),
  })
  .superRefine(({ nature, remplacement }, ctx) => {
    if (nature === "rectification" && !remplacement)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["remplacement"],
        message: "Indiquez le ticket de remplacement.",
      });
    if (nature === "annulation" && remplacement)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["remplacement"],
        message: "Une annulation ne crée pas de ticket de remplacement.",
      });
  });
export type BordereauApportRectifierInput = z.infer<typeof bordereauApportRectifierInput>;

/** Input `vendange.listerBordereaux` — filtre campagne optionnel. */
export const bordereauApportListerInput = z
  .object({
    campagne_id: idSchema.optional(),
  })
  .optional();
export type BordereauApportListerInput = z.infer<typeof bordereauApportListerInput>;

/** Input `vendange.statsResume` — pesées et tickets de la campagne, séparés par source. */
export const vendangeStatsResumeInput = z.object({
  campagne_id: idSchema,
});
export type VendangeStatsResumeInput = z.infer<typeof vendangeStatsResumeInput>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas de SORTIE (validation Zod en sortie de tRPC — defense in depth)
// ────────────────────────────────────────────────────────────────────────────

/** Ligne de ventilation renvoyée par l'API. */
export const ventilationSortieSchema = z.object({
  id: idSchema,
  sous_parcelle_id: idSchema,
  poids_reparti_kg: z.string(),
});

/** Part réellement engagée dans un marc de pressurage actif. */
export const affectationPressuragePeseeSortieSchema = z.object({
  marc_id: idSchema,
  poids_affecte_kg: z.string(),
});

/**
 * Pesée renvoyée par l'API. Les NUMERIC arrivent en `string` (précision
 * préservée). `ventilation` listée seulement sur la fiche détail.
 */
export const peseeSortieSchema = z.object({
  id: idSchema,
  campagne_id: idSchema,
  parcelle_id: idSchema,
  sous_parcelle_id: idSchema.nullable(),
  cepage_id: idSchema,
  cru_commune_insee: z.string().nullable(),
  date_pesee: z.string(),
  heure_pesee: z.string().nullable(),
  poids_kg: z.string(),
  contenant: contenantSchema,
  nb_contenants: z.number().int().nullable(),
  tare_kg: z.string().nullable(),
  numero_bon: z.string().nullable(),
  destination: destinationSchema.nullable(),
  poids_brut_kg: z.string().nullable().optional(),
  tares_caisses: taresCaissesSchema.nullable().optional(),
  pesee_origine_id: idSchema.nullable().optional(),
  operateur_id: idSchema.nullable(),
  statut: peseeStatutSchema,
  notes: z.string().nullable(),
  /**
   * Projection canonique de `marc_origines`. Une pesée peut alimenter
   * plusieurs marcs et n'être que partiellement affectée ; le lien historique
   * `pesees.marc_id` n'est donc volontairement plus exposé.
   */
  affectations_pressurage: z.array(affectationPressuragePeseeSortieSchema),
  poids_affecte_pressurage_kg: z.string(),
  poids_restant_pressurage_kg: z.string(),
  cree_le: z.date(),
});
export type PeseeSortie = z.infer<typeof peseeSortieSchema>;

/** Fiche détail pesée + ventilation imbriquée. */
export const peseeDetailSortieSchema = peseeSortieSchema.extend({
  ventilation: z.array(ventilationSortieSchema),
});
export type PeseeDetailSortie = z.infer<typeof peseeDetailSortieSchema>;

/** Regroupement (totaux kg) pour les widgets / la DR. */
export const peseeGroupeSortieSchema = z.object({
  cle: z.string(),
  libelle: z.string().nullable(),
  poids_total_kg: z.number(),
  nb_pesees: z.number().int(),
});
export type PeseeGroupeSortie = z.infer<typeof peseeGroupeSortieSchema>;

/**
 * Sortie `vendange.listerPesees` — liste plate + regroupements optionnels
 * (présents seulement si `groupe=true`).
 */
export const peseeListeSortieSchema = z.object({
  pesees: z.array(peseeSortieSchema),
  groupes_par_cepage: z.array(peseeGroupeSortieSchema).optional(),
  groupes_par_cru: z.array(peseeGroupeSortieSchema).optional(),
  groupes_par_destination: z.array(peseeGroupeSortieSchema).optional(),
});
export type PeseeListeSortie = z.infer<typeof peseeListeSortieSchema>;

/** Sortie `vendange.creerPesee` — id + date de création. */
export const peseeCreerSortieSchema = z.object({
  id: idSchema,
  cree_le: z.date(),
});

/** Sortie `vendange.validerPesee` — id + statut résultant. */
export const peseeValiderSortieSchema = z.object({
  id: idSchema,
  statut: peseeStatutSchema,
});

/**
 * Sortie `vendange.corrigerPesee` — la pesée d'origine (marquée `corrigee`) et
 * la nouvelle pesée de remplacement (registre inaltérable, opération inverse).
 */
export const peseeCorrigerSortieSchema = z.object({
  pesee_corrigee_id: idSchema,
  nouvelle_pesee_id: idSchema,
  cree_le: z.date(),
});

/** Origine de ticket renvoyée par l'API ; les champs v2 sont facultatifs. */
export const bordereauApportLigneSortieSchema = z.object({
  id: idSchema,
  libelle_origine: z.string().nullable(),
  parcelle_id: idSchema.nullable(),
  sous_parcelle_id: idSchema.nullable(),
  cepage_id: idSchema.nullable(),
  cru_commune_insee: z.string().nullable(),
  poids_kg: z.string().nullable(),
});

export const ticketExterieurStatutSchema = z.enum(["enregistre", "rectifie", "annule"]);

/** Ticket extérieur unifié. Les alias v1 évitent de casser une PWA déjà chargée. */
export const bordereauApportSortieSchema = z.object({
  id: idSchema,
  version_schema: z.union([z.literal(1), z.literal(2)]),
  campagne_id: idSchema,
  cooperative: z.string(),
  emetteur: z.string(),
  site_emetteur: z.string().nullable(),
  operateur_apporteur: z.string().nullable(),
  revendications_particulieres: z.string().nullable(),
  retranscription_complete: z.boolean(),
  numero: z.string().nullable(),
  date_apport: z.string(),
  date_pesee: z.string(),
  heure_pesee: z.string().nullable(),
  usage: ticketExterieurUsageSchema,
  poids_net_kg: z.string(),
  poids_non_ventile_kg: z.string(),
  statut: ticketExterieurStatutSchema,
  remplace_par_id: idSchema.nullable(),
  remplace_id: idSchema.nullable(),
  motif_rectification: z.string().nullable(),
  rectifie_le: z.date().nullable(),
  auteur_membre_id: idSchema.nullable(),
  notes: z.string().nullable(),
  cree_le: z.date(),
  lignes: z.array(bordereauApportLigneSortieSchema),
});
export type BordereauApportSortie = z.infer<typeof bordereauApportSortieSchema>;

/** Sortie `vendange.listerBordereaux`. */
export const bordereauApportListeSortieSchema = z.array(bordereauApportSortieSchema);

/** Sortie `vendange.creerBordereauApport` — id + date de création. */
export const bordereauApportCreerSortieSchema = z.object({
  id: idSchema,
  cree_le: z.date(),
});

/** Sortie d'une annulation ou rectification append-only. */
export const bordereauApportRectifierSortieSchema = z.object({
  bordereau_origine_id: idSchema,
  bordereau_remplacement_id: idSchema.nullable(),
  correction_id: idSchema,
  cree_le: z.date(),
});

/** Un ticket peut porter plusieurs lignes ; leur nombre n'est pas un nombre de pesées. */
const ticketGroupeSortieSchema = peseeGroupeSortieSchema
  .omit({ nb_pesees: true })
  .extend({ nb_lignes: z.number().int() });

/**
 * Totaux factuels par source. Sans rapprochement pesée/ticket, les deux blocs
 * peuvent décrire les mêmes raisins : ils ne forment pas une récolte consolidée.
 */
export const vendangeStatsResumeSortieSchema = z.object({
  pesees_domaine: z.object({
    poids_total_kg: z.number(),
    par_cepage: z.array(peseeGroupeSortieSchema),
    par_cru: z.array(peseeGroupeSortieSchema),
  }),
  tickets_exterieurs: z.object({
    poids_total_kg: z.number(),
    poids_ventile_kg: z.number(),
    poids_non_ventile_kg: z.number(),
    nb_tickets: z.number().int(),
    par_cepage: z.array(ticketGroupeSortieSchema),
    par_cru: z.array(ticketGroupeSortieSchema),
  }),
  /**
   * Repère informatif sur l'unique source renseignée, pas une récolte consolidée.
   * Null si pesées et tickets coexistent, ou si le plafond de campagne ou la
   * surface productive manque. Ne bloque jamais la saisie.
   */
  alerte_rendement: z
    .object({
      rendement_max_kg_ha: z.number(),
      rendement_constate_kg_ha: z.number(),
      surface_ares: z.number(),
      depassement: z.boolean(),
    })
    .nullable(),
});
export type VendangeStatsResumeSortie = z.infer<typeof vendangeStatsResumeSortieSchema>;
